import { z } from 'zod';
import { CompletionBasis, ReportTier } from './enums.js';
import { IsoDateTime } from './common.js';

export const MetricId = z.enum([
  'requests_created',
  'request_first_response',
  'request_acceptance_rate',
  'requests_fulfilled',
  'request_lead_time',
  'work_completed',
  'work_cycle_time',
  'work_in_progress',
  'blocked_time',
  'review_wait',
  'integration_to_production',
  'deployment_frequency',
  'deployment_failure_rate',
  'reopen_rate',
  'recovery_time',
  'contribution_activity',
  'code_additions',
  'code_deletions',
  'capture_coverage',
  'requester_confirmation',
  'integrated_ai_usage',
]);
export type MetricId = z.infer<typeof MetricId>;

export const ReportGroupBy = z.enum(['none', 'team', 'project', 'type', 'priority', 'week', 'month', 'repository', 'service', 'person', 'outcome', 'source']);
export type ReportGroupBy = z.infer<typeof ReportGroupBy>;

export const ReportDefinitionView = z.object({
  id: MetricId,
  formulaVersion: z.string(),
  tier: ReportTier,
  unit: z.enum(['count', 'seconds', 'ratio', 'tokens', 'lines']),
  population: z.string(),
  numerator: z.string().nullable(),
  denominator: z.string().nullable(),
  timeBasis: z.string(),
  exclusions: z.string(),
  attribution: z.string(),
  groupBy: z.array(ReportGroupBy),
  personGroupingTier: ReportTier,
  requiresCompletionBasis: z.boolean(),
});
export type ReportDefinitionView = z.infer<typeof ReportDefinitionView>;

export const QueryReport = z
  .object({
    reportId: MetricId,
    formulaVersion: z.string().min(1).max(20),
    dateFrom: IsoDateTime,
    dateTo: IsoDateTime,
    timezone: z.string().min(1).max(64),
    groupBy: ReportGroupBy.optional(),
    completionBasis: CompletionBasis.optional(),
    firstCompletionOnly: z.boolean().optional(),
    filters: z
      .object({
        projectId: z.uuid().optional(),
        teamId: z.uuid().optional(),
        type: z.string().max(40).optional(),
        repositoryId: z.uuid().optional(),
        userId: z.uuid().optional(),
        source: z.string().max(40).optional(),
      })
      .strict()
      .optional(),
    cursor: z.string().max(512).optional(),
  })
  .strict();
export type QueryReport = z.infer<typeof QueryReport>;

export const ReportRow = z.object({
  group: z.string().nullable(),
  groupLabel: z.string().nullable(),
  value: z.number().nullable(),
  count: z.number().int(),
  numerator: z.number().nullable(),
  denominator: z.number().nullable(),
  p50: z.number().nullable(),
  p85: z.number().nullable(),
  unavailable: z.number().int(),
});
export type ReportRow = z.infer<typeof ReportRow>;

export const ReportCoverage = z.object({
  status: z.enum(['COMPLETE', 'PARTIAL', 'UNKNOWN']),
  notes: z.array(z.string()),
  /** Stable codes and parameters for the notes above, so clients can localize them. */
  noteCodes: z.array(z.object({ code: z.string(), params: z.record(z.string(), z.union([z.string(), z.number()])) })),
  sources: z.array(z.object({ source: z.string(), mode: z.string(), lastSuccessAt: IsoDateTime.nullable() })),
});

export const ReportResult = z.object({
  reportId: MetricId,
  formulaVersion: z.string(),
  tier: ReportTier,
  timezone: z.string(),
  dateFrom: IsoDateTime,
  dateTo: IsoDateTime,
  groupBy: ReportGroupBy,
  rows: z.array(ReportRow),
  total: ReportRow,
  overlapping: z.boolean(),
  coverage: ReportCoverage,
  watermark: IsoDateTime,
  nextCursor: z.string().nullable(),
});
export type ReportResult = z.infer<typeof ReportResult>;

export const CreateReportExport = QueryReport.extend({ format: z.literal('csv') }).strict();
export type CreateReportExport = z.infer<typeof CreateReportExport>;
